import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useCompany } from "@/contexts/BusinessSettingsContext";
import { QuotePopup } from "@/components/QuotePopup";

interface NavLink {
  href: string;
  label: string;
  featureKey?: keyof ReturnType<typeof useFeatureFlags>;
}

const allNavLinks: NavLink[] = [
  { href: "/slabs", label: "Slab Gallery" },
  { href: "/portfolio", label: "Portfolio" },
  { href: "/quote", label: "Get Estimate", featureKey: "instant_quote" },
  { href: "/book", label: "Book Consultation", featureKey: "online_booking" },
];

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const flags = useFeatureFlags();
  const co = useCompany();

  const navLinks = allNavLinks.filter(
    (link) => !link.featureKey || flags[link.featureKey]
  );

  // Split company name for accent styling — "Altar Stones" → "Altar" + "Stones"
  const nameParts = co.companyName.split(" ");
  const firstName = nameParts.slice(0, -1).join(" ") || nameParts[0];
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground text-xs py-1.5">
        <div className="container flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a href={`tel:${co.companyPhone.replace(/\D/g, "")}`} className="flex items-center gap-1 hover:text-accent transition-colors">
              <Phone className="h-3 w-3" /> {co.companyPhone}
            </a>
            <span className="hidden sm:flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {co.companyAddress}
            </span>
          </div>
          {co.licensedInsuredEnabled && (
            <span className="hidden md:block">{co.licensedInsuredText}</span>
          )}
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="container flex items-center justify-between gap-3 h-16">
          <Link to="/" className="font-display text-xl font-semibold tracking-tight shrink-0">
            {firstName} {lastName && <span className="text-accent">{lastName}</span>}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 min-w-0">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  location.pathname === link.href
                    ? "text-accent"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {link.label}
              </Link>
            ))}
            {flags.customer_portal && (
              <Button asChild size="sm" className="ml-2 shrink-0 whitespace-nowrap bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/login">Client Portal</Link>
              </Button>
            )}
          </nav>

          {/* Mobile toggle */}
          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle mobile navigation menu">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>


        {/* Mobile nav */}
        {mobileOpen && (
          <nav className="lg:hidden border-t bg-background p-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "block px-3 py-2.5 text-sm font-medium rounded-md",
                  location.pathname === link.href
                    ? "bg-secondary text-accent"
                    : "text-muted-foreground"
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t mt-3 space-y-1 text-sm text-muted-foreground px-3">
              <a href={`tel:${co.companyPhone.replace(/\D/g, "")}`} className="flex items-center gap-2">
                <Phone className="h-3 w-3" /> {co.companyPhone}
              </a>
              <a href={`mailto:${co.companyEmail}`} className="flex items-center gap-2">
                {co.companyEmail}
              </a>
            </div>
            {flags.customer_portal && (
              <Button asChild size="sm" className="w-full mt-2 bg-accent text-accent-foreground">
                <Link to="/login" onClick={() => setMobileOpen(false)}>Client Portal</Link>
              </Button>
            )}
          </nav>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <h3 className="font-display text-xl font-semibold mb-3">
                {firstName} {lastName && <span className="text-accent">{lastName}</span>}
              </h3>
              <p className="text-primary-foreground/70 text-sm max-w-sm">
                Altar Stone Countertops — Premium quartz, marble, quartzite and granite countertop fabrication and installation in Sarasota, FL. Serving Bradenton, Venice, Port Charlotte, Fort Myers, Naples and all of Southwest Florida. Licensed & Insured. Call (941) 541-3348.
              </p>
              <div className="mt-4 space-y-1 text-sm text-primary-foreground/70">
                <a href={`tel:${co.companyPhone.replace(/\D/g, "")}`} className="block hover:text-accent transition-colors">
                  {co.companyPhone}
                </a>
                <a href={`mailto:${co.companyEmail}`} className="block hover:text-accent transition-colors">
                  {co.companyEmail}
                </a>
                <p>{co.companyAddress}</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-3 text-accent">Quick Links</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/slabs" className="hover:text-accent transition-colors">Slab Gallery</Link></li>
                <li><Link to="/portfolio" className="hover:text-accent transition-colors">Portfolio</Link></li>
                {flags.instant_quote && (
                  <li><Link to="/quote" className="hover:text-accent transition-colors">Get Estimate</Link></li>
                )}
                <li><Link to="/blog" className="hover:text-accent transition-colors">Countertop Guides</Link></li>
                <li><Link to="/faq" className="hover:text-accent transition-colors">FAQ</Link></li>
                <li><a href="https://g.page/r/CS4mHXcewSFZEAE/review" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Leave a Google Review ★</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-3 text-accent">Sarasota Services</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/granite-countertops-sarasota" className="hover:text-accent transition-colors">Granite Countertops</Link></li>
                <li><Link to="/quartz-countertops-sarasota" className="hover:text-accent transition-colors">Quartz Countertops</Link></li>
                <li><Link to="/quartzite-countertops-sarasota" className="hover:text-accent transition-colors">Quartzite Countertops</Link></li>
                <li><Link to="/marble-countertops-sarasota" className="hover:text-accent transition-colors">Marble Countertops</Link></li>
                <li><Link to="/countertop-installation-sarasota" className="hover:text-accent transition-colors">Installation Sarasota</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-3 text-accent">Legal</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/legal/terms" className="hover:text-accent transition-colors">Terms of Service</Link></li>
                <li><Link to="/legal/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link></li>
                <li><Link to="/legal/warranty" className="hover:text-accent transition-colors">Warranty</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-primary-foreground/10 mt-8 pt-6 text-xs text-primary-foreground/50 text-center">
            © {new Date().getFullYear()} {co.companyName} Countertops. All rights reserved.
            {co.licensedInsuredEnabled && ` ${co.licensedInsuredText}.`}
          </div>
        </div>
      </footer>

      {/* Conversion helpers */}
      <QuotePopup />
      {/* Spacer so the sticky mobile bar never covers footer content */}
      <div className="h-14 lg:hidden" aria-hidden="true" />
    </div>
  );
}
